import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { Notify, toast } from "@/components/ui/notify";
import { CreateUserRequestSchema, type CreateUserRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { useAuthStore } from "@/stores/auth.store";
import { useUsers, useCreateUser, useDeleteUser } from "@/features/users/hooks";
import { getUserRoleLabel, formatRelativeTime } from "@/lib/display-labels";
import { getFriendlyErrorMessage } from "@/lib/api-client";

export function UsersPage() {
  const me = useAuthStore((s) => s.user)!;
  const usersQuery = useUsers();
  const { data: users = [], isLoading } = usersQuery;
  const createUser = useCreateUser();
  const deleteUser = useDeleteUser();
  const [showForm, setShowForm] = useState(false);

  const form = useForm<CreateUserRequest>({
    resolver: zodResolver(CreateUserRequestSchema),
    defaultValues: { name: "", email: "", password: "", role: "OPERADOR" },
  });

  async function onSubmit(values: CreateUserRequest) {
    try {
      await createUser.mutateAsync(values);
      toast.success("Usuário criado.");
      form.reset();
      setShowForm(false);
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao criar usuário.", { description: msg });
    }
  }

  async function onDelete(id: string, name: string) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Excluir usuário "${name}"?`,
      description: "O histórico do usuário permanece preservado (exclusão reversível).",
      confirmLabel: "Excluir",
      typeToConfirm: name,
    });
    if (!ok) return;
    try {
      await deleteUser.mutateAsync(id);
      toast.success("Usuário excluído.");
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao excluir usuário.", { description: msg });
    }
  }

  return (
    <section>
      <PageHeader
        title="Usuários"
        subtitle="Gerencie os acessos da sua organização."
        actions={
          me.role === "GESTOR" ? (
            <Button onClick={() => setShowForm((v) => !v)}>
              {showForm ? "Cancelar" : "Novo usuário"}
            </Button>
          ) : null
        }
      />

      {showForm && me.role === "GESTOR" ? (
        <Card className="mb-6 animate-scale-in">
          <CardHeader>
            <CardTitle>Novo usuário</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
              className="grid gap-4 md:grid-cols-2"
            >
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input id="name" {...form.register("name")} />
                {form.formState.errors.name ? (
                  <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" {...form.register("email")} />
                {form.formState.errors.email ? (
                  <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input id="password" type="password" {...form.register("password")} />
                {form.formState.errors.password ? (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.password.message}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Perfil</Label>
                <Select id="role" {...form.register("role")}>
                  <option value="OPERADOR">Operador</option>
                  <option value="GESTOR">Gestor</option>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={createUser.isPending}>
                  {createUser.isPending ? "Criando…" : "Criar usuário"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {usersQuery.isError ? (
        <Notify.QueryError error={usersQuery.error} onRetry={() => void usersQuery.refetch()} />
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        <DataTable
          data={users}
          rowKey={(u) => u.id}
          emptyState={
            me.role === "GESTOR"
              ? "Crie o primeiro usuário com o botão acima."
              : "Nenhum usuário cadastrado."
          }
          columns={[
            { header: "Nome", accessor: (u) => u.name },
            { header: "E-mail", accessor: (u) => u.email },
            {
              header: "Perfil",
              accessor: (u) => (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs uppercase tracking-wider">
                  {getUserRoleLabel(u.role)}
                </span>
              ),
            },
            {
              header: "Último login",
              accessor: (u) => (u.lastLoginAt ? formatRelativeTime(u.lastLoginAt) : "—"),
              className: "text-muted-foreground",
            },
            {
              header: "",
              accessor: (u) =>
                me.role === "GESTOR" && u.id !== me.id ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Excluir ${u.name}`}
                    onClick={() => void onDelete(u.id, u.name)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null,
              className: "text-right",
            },
          ]}
        />
      )}
    </section>
  );
}
